import React, { useState } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import {
    SidebarFilterDto,
    UpdateSidebarFilterDto,
    SidebarFilterType,
    SpecificationDto,
    SidebarFilterOptionDto,
} from '../../types';

interface SidebarFilterModalProps {
    filter: SidebarFilterDto;
    enumSpecifications: SpecificationDto[];
    onClose: () => void;
    onSave: (id: number, dto: UpdateSidebarFilterDto) => Promise<void>;
    onSync: (id: number) => Promise<SidebarFilterDto>;
}

const SidebarFilterModal: React.FC<SidebarFilterModalProps> = ({
    filter,
    onClose,
    onSave,
    onSync,
}) => {
    const [form, setForm] = useState<UpdateSidebarFilterDto>({
        isEnabled: filter.isEnabled,
        sortOrder: filter.sortOrder,
        titleTranslationKey: filter.titleTranslationKey ?? '',
        rangeMin: filter.rangeMin,
        rangeMax: filter.rangeMax,
        rangeStep: filter.rangeStep,
        options: filter.options.map((o) => ({
            id: o.id,
            icon: o.icon,
            labelTranslationKey: o.labelTranslationKey,
            isEnabled: o.isEnabled,
            sortOrder: o.sortOrder,
            minPrice: o.minPrice,
            maxPrice: o.maxPrice,
        })),
    });
    const [syncing, setSyncing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [emojiTarget, setEmojiTarget] = useState<number | null>(null);

    const updateOption = (id: number, patch: Partial<SidebarFilterOptionDto>) => {
        setForm((prev) => ({
            ...prev,
            options: prev.options.map((o) => (o.id === id ? { ...o, ...patch } : o)),
        }));
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            const updated = await onSync(filter.id);
            setForm({
                isEnabled: updated.isEnabled,
                sortOrder: updated.sortOrder,
                titleTranslationKey: updated.titleTranslationKey ?? '',
                rangeMin: updated.rangeMin,
                rangeMax: updated.rangeMax,
                rangeStep: updated.rangeStep,
                options: updated.options.map((o) => ({
                    id: o.id,
                    icon: o.icon,
                    labelTranslationKey: o.labelTranslationKey,
                    isEnabled: o.isEnabled,
                    sortOrder: o.sortOrder,
                    minPrice: o.minPrice,
                    maxPrice: o.maxPrice,
                })),
            });
        } finally {
            setSyncing(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(filter.id, form);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const canSync =
        filter.type === SidebarFilterType.Specification ||
        filter.type === SidebarFilterType.GrowthPhase ||
        filter.type === SidebarFilterType.Condition;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-800">
                        Edit Sidebar Filter — {filter.typeName}
                        {filter.specificationName ? ` (${filter.specificationName})` : ''}
                    </h2>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.isEnabled}
                            onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                        />
                        Enabled
                    </label>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Sort order</label>
                        <input
                            type="number"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            value={form.sortOrder}
                            onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Title translation key</label>
                        <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            value={form.titleTranslationKey ?? ''}
                            onChange={(e) => setForm({ ...form, titleTranslationKey: e.target.value })}
                            placeholder="filter.price"
                        />
                    </div>

                    {filter.type === SidebarFilterType.Price && (
                        <>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Range min (€)</label>
                                <input
                                    type="number"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    value={form.rangeMin ?? 0}
                                    onChange={(e) => setForm({ ...form, rangeMin: Number(e.target.value) })}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Range max (€)</label>
                                <input
                                    type="number"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    value={form.rangeMax ?? 5000}
                                    onChange={(e) => setForm({ ...form, rangeMax: Number(e.target.value) })}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Step</label>
                                <input
                                    type="number"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                    value={form.rangeStep ?? 50}
                                    onChange={(e) => setForm({ ...form, rangeStep: Number(e.target.value) })}
                                />
                            </div>
                        </>
                    )}
                </div>

                {form.options.length > 0 && (
                    <div className="mt-6">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800">Options</h3>
                            {canSync && (
                                <button
                                    type="button"
                                    onClick={handleSync}
                                    disabled={syncing}
                                    className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    {syncing ? 'Syncing…' : 'Sync options'}
                                </button>
                            )}
                        </div>
                        <div className="space-y-3">
                            {form.options
                                .slice()
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map((option) => {
                                    const source = filter.options.find((o) => o.id === option.id);
                                    return (
                                        <div key={option.id} className="rounded border border-gray-200 p-3">
                                            <div className="mb-2 text-sm font-medium text-gray-800">
                                                Value: {source?.value ?? option.id}
                                                {source?.minAgeMonths != null && source?.maxAgeMonths != null && (
                                                    <span className="ml-2 text-gray-500">
                                                        ({source.minAgeMonths}–{source.maxAgeMonths} mo)
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={option.isEnabled}
                                                        onChange={(e) => updateOption(option.id, { isEnabled: e.target.checked })}
                                                    />
                                                    Enabled
                                                </label>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Sort order</label>
                                                    <input
                                                        type="number"
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                        value={option.sortOrder}
                                                        onChange={(e) => updateOption(option.id, { sortOrder: Number(e.target.value) })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Label translation key</label>
                                                    <input
                                                        type="text"
                                                        className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                                        value={option.labelTranslationKey ?? ''}
                                                        onChange={(e) => updateOption(option.id, { labelTranslationKey: e.target.value })}
                                                    />
                                                </div>
                                                <div className="md:col-span-3">
                                                    <label className="mb-1 block text-xs text-gray-600">Icon (emoji)</label>
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            className="rounded border border-gray-300 px-2 py-1 text-sm"
                                                            onClick={() => setEmojiTarget(emojiTarget === option.id ? null : option.id)}
                                                        >
                                                            Pick emoji
                                                        </button>
                                                        <span className="text-2xl">{option.icon ?? '—'}</span>
                                                    </div>
                                                    {emojiTarget === option.id && (
                                                        <div className="mt-2">
                                                            <EmojiPicker
                                                                onEmojiClick={(data: EmojiClickData) => {
                                                                    updateOption(option.id, { icon: data.emoji });
                                                                    setEmojiTarget(null);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                <div className="mt-8 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded px-4 py-2 text-gray-600 hover:bg-gray-100">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SidebarFilterModal;